import { createAuthClient } from 'better-auth/react';
import { magicLinkClient } from 'better-auth/client/plugins';
import { useSyncExternalStore } from 'react';
import { getMeta, setMeta, wipeLocalData } from '../db/db';
import { adoptHousehold, syncEngine } from '../sync/engine';
import { disablePush } from './push';

export const authClient = createAuthClient({
  plugins: [magicLinkClient()],
});

export interface CachedSession {
  userId: string;
  name: string;
  email: string;
  image: string | null;
  householdId: string;
}

// Sesja trzymana lokalnie, żeby aplikacja uruchomiona bez internetu działała dalej.
// Wylogowujemy tylko po jednoznacznym 401 z serwera.
const KEY = 'odhaczka.session';

type AuthState = { status: 'loading' } | { status: 'anonymous' } | { status: 'signed-in'; session: CachedSession };

let state: AuthState = readCache();
const listeners = new Set<() => void>();

function readCache(): AuthState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { status: 'signed-in', session: JSON.parse(raw) as CachedSession };
  } catch {
    // brak dostępu do localStorage — sprawdzimy sesję na serwerze
  }
  return { status: 'loading' };
}

function setState(next: AuthState) {
  state = next;
  try {
    if (next.status === 'signed-in') localStorage.setItem(KEY, JSON.stringify(next.session));
    else if (next.status === 'anonymous') localStorage.removeItem(KEY);
  } catch {
    // ignorujemy
  }
  for (const fn of listeners) fn();
}

/** Id zalogowanego użytkownika (do podpisywania zmian); null przed zalogowaniem. */
export function currentUserId(): string | null {
  return state.status === 'signed-in' ? state.session.userId : null;
}

/** Gospodarstwo z ostatnio sprawdzonej sesji. */
export function currentHouseholdId(): string | null {
  return state.status === 'signed-in' ? state.session.householdId : null;
}

/** Nasłuch zmian stanu logowania (poza Reactem, np. socket /api/live). */
export function onAuthChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}

export function useAuth(): AuthState {
  return useSyncExternalStore(onAuthChange, () => state);
}

/** Sprawdza sesję na serwerze; bez sieci zostawia sesję z cache. */
export async function refreshSession(): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/api/me', { credentials: 'same-origin' });
  } catch {
    if (state.status === 'loading') setState({ status: 'anonymous' });
    return;
  }
  if (res.status === 401) {
    await signedOut();
    return;
  }
  if (!res.ok) {
    if (state.status === 'loading') setState({ status: 'anonymous' });
    return;
  }
  const me = (await res.json()) as {
    user: { id: string; name: string; email: string; image?: string | null };
    householdId: string;
  };
  // Dane lokalne innego konta (inna osoba na tym telefonie) — zaczynamy od zera.
  // Zmiana gospodarstwa (dołączenie do domu) niczego nie kasuje: kursor zeruje adoptHousehold.
  const localUser = (await getMeta<string>('userId')) ?? (state.status === 'signed-in' ? state.session.userId : undefined);
  const localHousehold = await getMeta<string>('householdId');
  // Dane sprzed etapu 2 nie znają właściciela — wtedy jak dawniej rozstrzyga gospodarstwo.
  const otherAccount = localUser ? localUser !== me.user.id : !!localHousehold && localHousehold !== me.householdId;
  if (otherAccount) await wipeLocalData();
  await setMeta('userId', me.user.id);
  setState({
    status: 'signed-in',
    session: {
      userId: me.user.id,
      name: me.user.name,
      email: me.user.email,
      image: me.user.image ?? null,
      householdId: me.householdId,
    },
  });
  await adoptHousehold(me.householdId);
}

async function signedOut() {
  setState({ status: 'anonymous' });
}

export async function signOut(): Promise<void> {
  try {
    // Najpierw próbujemy wysłać niezsynchronizowane zmiany — po wylogowaniu dane lokalne znikają.
    await syncEngine.run();
    // Powiadomienia z tego urządzenia nie mogą trafiać do następnej osoby, która się na nim zaloguje.
    await disablePush().catch(() => {});
    await authClient.signOut();
  } catch {
    // offline — i tak czyścimy lokalnie
  }
  await wipeLocalData();
  setState({ status: 'anonymous' });
}

syncEngine.onUnauthorized = () => void signedOut();
